/**
 * Professional HTML email campaign seeds for Beyund AI marketing.
 * Merge tags: {{user_name}}, {{user_email}}, {{login_url}}, {{site_url}}, {{unsubscribe_url}}, {{campaign_title}}, {{date}}
 */

const FOOTER = `
  <tr>
    <td style="padding:24px 32px;background:#f8fafc;border-top:1px solid #e2e8f0;">
      <p style="margin:0 0 8px;font-size:12px;line-height:1.5;color:#94a3b8;font-family:Arial,Helvetica,sans-serif;">
        You’re receiving this because you opted into product updates from Beyund AI.
      </p>
      <p style="margin:0;font-size:12px;line-height:1.5;color:#94a3b8;font-family:Arial,Helvetica,sans-serif;">
        <a href="{{unsubscribe_url}}" style="color:#64748b;text-decoration:underline;">Manage email preferences</a>
        &nbsp;·&nbsp;
        <a href="{{login_url}}" style="color:#64748b;text-decoration:underline;">Open dashboard</a>
      </p>
    </td>
  </tr>
`;

function shell({ accent, eyebrow, headline, bodyRows, ctaLabel, ctaUrl }) {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>{{campaign_title}}</title>
</head>
<body style="margin:0;padding:0;background:#f1f5f9;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background:#f1f5f9;padding:32px 12px;">
    <tr>
      <td align="center">
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width:560px;background:#ffffff;border-radius:12px;overflow:hidden;border:1px solid #e2e8f0;">
          <tr>
            <td style="padding:28px 32px 20px;background:${accent};">
              <p style="margin:0 0 8px;font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:rgba(255,255,255,0.75);font-family:Arial,Helvetica,sans-serif;font-weight:700;">
                ${eyebrow}
              </p>
              <h1 style="margin:0;font-size:24px;line-height:1.3;color:#ffffff;font-family:Arial,Helvetica,sans-serif;font-weight:700;">
                ${headline}
              </h1>
            </td>
          </tr>
          <tr>
            <td style="padding:28px 32px;font-family:Arial,Helvetica,sans-serif;">
              <p style="margin:0 0 16px;font-size:15px;line-height:1.6;color:#334155;">
                Hi {{user_name}},
              </p>
              ${bodyRows}
              <table role="presentation" cellspacing="0" cellpadding="0" style="margin:28px 0 8px;">
                <tr>
                  <td style="border-radius:8px;background:${accent};">
                    <a href="${ctaUrl}" style="display:inline-block;padding:12px 22px;font-size:14px;font-weight:700;color:#ffffff;text-decoration:none;font-family:Arial,Helvetica,sans-serif;">
                      ${ctaLabel}
                    </a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          ${FOOTER}
        </table>
        <p style="margin:16px 0 0;font-size:11px;color:#94a3b8;font-family:Arial,Helvetica,sans-serif;">
          © Beyund AI · {{date}}
        </p>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

export const CAMPAIGN_TEMPLATES = [
  {
    id: "affiliate-launch",
    category: "Growth",
    badge: "Affiliate",
    title: "Invite friends & earn credits",
    subject: "{{user_name}}, earn free credits when friends join Beyund AI",
    description: "Announce the affiliate program — share link, earn credits per signup.",
    audience: "marketing_opt_in",
    html_body: shell({
      accent: "#0f172a",
      eyebrow: "Affiliate program",
      headline: "Invite friends. Earn credits.",
      bodyRows: `
        <p style="margin:0 0 14px;font-size:15px;line-height:1.6;color:#334155;">
          Your unique invite link is live. Share it with creators, clients, or your audience — when they create an account, <strong style="color:#0f172a;">you earn credits</strong> automatically.
        </p>
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="margin:18px 0;background:#ecfdf5;border:1px solid #a7f3d0;border-radius:10px;">
          <tr>
            <td style="padding:18px 20px;text-align:center;">
              <p style="margin:0 0 4px;font-size:12px;letter-spacing:0.08em;text-transform:uppercase;font-weight:700;color:#047857;font-family:Arial,Helvetica,sans-serif;">
                Default reward
              </p>
              <p style="margin:0;font-size:28px;line-height:1.2;font-weight:800;color:#065f46;font-family:Arial,Helvetica,sans-serif;">
                +25 credits
              </p>
              <p style="margin:6px 0 0;font-size:13px;line-height:1.5;color:#047857;font-family:Arial,Helvetica,sans-serif;">
                per successful signup via your link
              </p>
            </td>
          </tr>
        </table>
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="margin:0 0 18px;border:1px solid #e2e8f0;border-radius:10px;overflow:hidden;">
          <tr>
            <td style="padding:14px 16px;background:#f8fafc;font-size:13px;color:#0f172a;font-weight:700;border-bottom:1px solid #e2e8f0;">How it works</td>
          </tr>
          <tr>
            <td style="padding:12px 16px;font-size:13px;line-height:1.55;color:#475569;border-bottom:1px solid #f1f5f9;">1. Open Affiliate in your dashboard and copy your link</td>
          </tr>
          <tr>
            <td style="padding:12px 16px;font-size:13px;line-height:1.55;color:#475569;border-bottom:1px solid #f1f5f9;">2. Share it (WhatsApp, socials, email, or with clients)</td>
          </tr>
          <tr>
            <td style="padding:12px 16px;font-size:13px;line-height:1.55;color:#475569;">3. When they sign up, credits land in your balance</td>
          </tr>
        </table>
        <p style="margin:0;font-size:15px;line-height:1.6;color:#334155;">
          No forms. No waiting. Your invite history and earnings stay in one place under <strong style="color:#0f172a;">Affiliate</strong>.
        </p>`,
      ctaLabel: "Get my invite link →",
      ctaUrl: "{{login_url}}?page=affiliate",
    }),
  },
  {
    id: "gift-share-credits",
    category: "Growth",
    badge: "Gifting",
    title: "Gift & share credits",
    subject: "{{user_name}}, gift a plan or share credits with anyone on Beyund AI",
    description: "Announce public gift credits + transfer credits from Billing.",
    audience: "marketing_opt_in",
    html_body: `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>{{campaign_title}}</title>
</head>
<body style="margin:0;padding:0;background:#f1f5f9;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background:#f1f5f9;padding:32px 12px;">
    <tr>
      <td align="center">
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width:560px;background:#ffffff;border-radius:12px;overflow:hidden;border:1px solid #e2e8f0;">
          <tr>
            <td style="padding:28px 32px 20px;background:#0f172a;">
              <p style="margin:0 0 8px;font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:rgba(255,255,255,0.75);font-family:Arial,Helvetica,sans-serif;font-weight:700;">
                Credit gifting
              </p>
              <h1 style="margin:0;font-size:24px;line-height:1.3;color:#ffffff;font-family:Arial,Helvetica,sans-serif;font-weight:700;">
                Gift a plan. Share your credits.
              </h1>
            </td>
          </tr>
          <tr>
            <td style="padding:28px 32px;font-family:Arial,Helvetica,sans-serif;">
              <p style="margin:0 0 16px;font-size:15px;line-height:1.6;color:#334155;">
                Hi {{user_name}},
              </p>

              <p style="margin:0 0 14px;font-size:15px;line-height:1.6;color:#334155;">
                You can now send creative power to teammates, clients, or friends — buy them a plan, or move credits from your balance straight to theirs.
              </p>

              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="margin:18px 0;background:#ecfdf5;border:1px solid #a7f3d0;border-radius:10px;">
                <tr>
                  <td style="padding:18px 20px;text-align:center;">
                    <p style="margin:0 0 4px;font-size:12px;letter-spacing:0.08em;text-transform:uppercase;font-weight:700;color:#047857;font-family:Arial,Helvetica,sans-serif;">
                      Two ways to give
                    </p>
                    <p style="margin:0;font-size:22px;line-height:1.25;font-weight:800;color:#065f46;font-family:Arial,Helvetica,sans-serif;">
                      Gift a plan · Share credits
                    </p>
                    <p style="margin:6px 0 0;font-size:13px;line-height:1.5;color:#047857;font-family:Arial,Helvetica,sans-serif;">
                      Instant delivery to any Beyund AI account by email
                    </p>
                  </td>
                </tr>
              </table>

              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="margin:0 0 18px;border:1px solid #e2e8f0;border-radius:10px;overflow:hidden;">
                <tr>
                  <td style="padding:14px 16px;background:#f8fafc;font-size:13px;color:#0f172a;font-weight:700;border-bottom:1px solid #e2e8f0;">How it works</td>
                </tr>
                <tr>
                  <td style="padding:12px 16px;font-size:13px;line-height:1.55;color:#475569;border-bottom:1px solid #f1f5f9;"><strong style="color:#0f172a;">Gift Credits</strong> — enter their email, pick a plan, pay securely. Credits go to their account (no login needed to send).</td>
                </tr>
                <tr>
                  <td style="padding:12px 16px;font-size:13px;line-height:1.55;color:#475569;border-bottom:1px solid #f1f5f9;"><strong style="color:#0f172a;">Share from Billing</strong> — transfer credits you already own to another user by email.</td>
                </tr>
                <tr>
                  <td style="padding:12px 16px;font-size:13px;line-height:1.55;color:#475569;">They get the balance. You keep creating together.</td>
                </tr>
              </table>

              <p style="margin:0;font-size:15px;line-height:1.6;color:#334155;">
                Perfect for agencies, collaborators, and surprise top-ups — all from one place under <strong style="color:#0f172a;">Gift Credits</strong> and <strong style="color:#0f172a;">Billing</strong>.
              </p>

              <table role="presentation" cellspacing="0" cellpadding="0" style="margin:28px 0 12px;">
                <tr>
                  <td style="border-radius:8px;background:#0f172a;">
                    <a href="{{site_url}}/gift-credits" style="display:inline-block;padding:12px 22px;font-size:14px;font-weight:700;color:#ffffff;text-decoration:none;font-family:Arial,Helvetica,sans-serif;">
                      Gift a plan →
                    </a>
                  </td>
                </tr>
              </table>
              <p style="margin:0;font-size:13px;line-height:1.5;color:#64748b;font-family:Arial,Helvetica,sans-serif;">
                Already logged in?
                <a href="{{login_url}}?page=billing" style="color:#0f172a;font-weight:700;text-decoration:underline;">Share credits from Billing</a>
              </p>
            </td>
          </tr>

          <tr>
            <td style="padding:24px 32px;background:#f8fafc;border-top:1px solid #e2e8f0;">
              <p style="margin:0 0 8px;font-size:12px;line-height:1.5;color:#94a3b8;font-family:Arial,Helvetica,sans-serif;">
                You’re receiving this because you opted into product updates from Beyund AI.
              </p>
              <p style="margin:0;font-size:12px;line-height:1.5;color:#94a3b8;font-family:Arial,Helvetica,sans-serif;">
                <a href="{{unsubscribe_url}}" style="color:#64748b;text-decoration:underline;">Manage email preferences</a>
                &nbsp;·&nbsp;
                <a href="{{site_url}}/gift-credits" style="color:#64748b;text-decoration:underline;">Gift Credits</a>
                &nbsp;·&nbsp;
                <a href="{{login_url}}?page=billing" style="color:#64748b;text-decoration:underline;">Billing</a>
              </p>
            </td>
          </tr>
        </table>
        <p style="margin:16px 0 0;font-size:11px;color:#94a3b8;font-family:Arial,Helvetica,sans-serif;">
          © Beyund AI · {{date}}
        </p>
      </td>
    </tr>
  </table>
</body>
</html>`,
  },
  {
    id: "feature-launch",
    category: "Product",
    badge: "Launch",
    title: "New feature launch",
    subject: "Just shipped: create more, faster inside Beyund AI",
    description: "Announce a major feature with a clear CTA to try it.",
    audience: "marketing_opt_in",
    html_body: shell({
      accent: "#0f172a",
      eyebrow: "Product update",
      headline: "New creative tools are live",
      bodyRows: `
        <p style="margin:0 0 14px;font-size:15px;line-height:1.6;color:#334155;">
          We’ve released updates that help you move from idea → finished video with fewer steps — including stronger Image Studio workflows and smoother Video Studio generation.
        </p>
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="margin:18px 0;border:1px solid #e2e8f0;border-radius:10px;overflow:hidden;">
          <tr>
            <td style="padding:14px 16px;background:#f8fafc;font-size:13px;color:#0f172a;font-weight:700;border-bottom:1px solid #e2e8f0;">What’s new</td>
          </tr>
          <tr>
            <td style="padding:12px 16px;font-size:13px;line-height:1.55;color:#475569;border-bottom:1px solid #f1f5f9;">Faster image-to-video handoff</td>
          </tr>
          <tr>
            <td style="padding:12px 16px;font-size:13px;line-height:1.55;color:#475569;border-bottom:1px solid #f1f5f9;">Cleaner studio controls for duration &amp; aspect</td>
          </tr>
          <tr>
            <td style="padding:12px 16px;font-size:13px;line-height:1.55;color:#475569;">More reliable exports for social formats</td>
          </tr>
        </table>
        <p style="margin:0;font-size:15px;line-height:1.6;color:#334155;">
          Open your dashboard and try a short clip today — the best way to feel the difference.
        </p>`,
      ctaLabel: "Try it in your dashboard →",
      ctaUrl: "{{login_url}}",
    }),
  },
  {
    id: "win-back",
    category: "Retention",
    badge: "Win-back",
    title: "We miss you — win-back",
    subject: "{{user_name}}, your next viral clip is waiting",
    description: "Re-engage inactive users with a soft nudge and clear next step.",
    audience: "free",
    html_body: shell({
      accent: "#1e3a5f",
      eyebrow: "Quick reminder",
      headline: "Ready when you are",
      bodyRows: `
        <p style="margin:0 0 14px;font-size:15px;line-height:1.6;color:#334155;">
          It’s been a little while since your last creation. Your workspace is still here — characters, clips, and projects ready to pick up where you left off.
        </p>
        <p style="margin:0 0 14px;font-size:15px;line-height:1.6;color:#334155;">
          Tip: start with a single vertical clip for TikTok / Reels. One strong hook beats a long edit when you’re getting back into flow.
        </p>
        <p style="margin:0;font-size:15px;line-height:1.6;color:#334155;">
          Jump back in — it only takes a few minutes to ship something worth posting.
        </p>`,
      ctaLabel: "Continue creating →",
      ctaUrl: "{{login_url}}",
    }),
  },
  {
    id: "upgrade-credits",
    category: "Monetization",
    badge: "Upgrade",
    title: "Upgrade / top-up credits",
    subject: "Need more credits? Keep creating without interruption",
    description: "Drive plan upgrades and credit purchases with urgency-free clarity.",
    audience: "free",
    html_body: shell({
      accent: "#065f46",
      eyebrow: "Billing",
      headline: "Keep the momentum going",
      bodyRows: `
        <p style="margin:0 0 14px;font-size:15px;line-height:1.6;color:#334155;">
          Running low on credits slows campaigns. Upgrade your plan or top up so Image Studio, Video Studio, and clipping stay available when inspiration hits.
        </p>
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="margin:18px 0;background:#ecfdf5;border-radius:10px;">
          <tr>
            <td style="padding:16px 18px;">
              <p style="margin:0 0 6px;font-size:13px;font-weight:700;color:#065f46;">Why teams upgrade</p>
              <p style="margin:0;font-size:13px;line-height:1.55;color:#047857;">
                Higher limits, fewer pauses mid-campaign, and priority access when you’re shipping ads on a deadline.
              </p>
            </td>
          </tr>
        </table>
        <p style="margin:0;font-size:15px;line-height:1.6;color:#334155;">
          Review plans in Billing — pick what matches your output this month.
        </p>`,
      ctaLabel: "View plans & credits →",
      ctaUrl: "{{login_url}}",
    }),
  },
  {
    id: "tips-newsletter",
    category: "Nurture",
    badge: "Tips",
    title: "Creator tips newsletter",
    subject: "3 prompts that make AI UGC feel more human",
    description: "Educational nurture email that builds trust and product habit.",
    audience: "marketing_opt_in",
    html_body: shell({
      accent: "#4c1d95",
      eyebrow: "Creator tips",
      headline: "Make AI UGC feel real",
      bodyRows: `
        <p style="margin:0 0 14px;font-size:15px;line-height:1.6;color:#334155;">
          Great ads don’t sound like ads. Use these three prompt habits inside Beyund AI this week:
        </p>
        <ol style="margin:0 0 16px;padding-left:20px;color:#334155;font-size:15px;line-height:1.65;">
          <li style="margin-bottom:8px;"><strong>Lead with a problem</strong> — open on the pain, not the product.</li>
          <li style="margin-bottom:8px;"><strong>One claim per clip</strong> — short verticals convert better than feature dumps.</li>
          <li style="margin-bottom:0;"><strong>End on action</strong> — tell viewers exactly what to do next.</li>
        </ol>
        <p style="margin:0;font-size:15px;line-height:1.6;color:#334155;">
          Open Video Studio, generate a 15–30s test, and A/B the first three seconds.
        </p>`,
      ctaLabel: "Open Video Studio →",
      ctaUrl: "{{login_url}}",
    }),
  },
  {
    id: "limited-offer",
    category: "Promo",
    badge: "Offer",
    title: "Limited-time offer",
    subject: "This week only: stretch your creative budget further",
    description: "Seasonal or flash promo — edit the offer copy before sending.",
    audience: "marketing_opt_in",
    html_body: shell({
      accent: "#9a3412",
      eyebrow: "Limited offer",
      headline: "A better week to create more",
      bodyRows: `
        <p style="margin:0 0 14px;font-size:15px;line-height:1.6;color:#334155;">
          For a short window, we’re highlighting plans that give you more room to test creatives — more variants, more hooks, more learning per campaign.
        </p>
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="margin:18px 0;border:1px dashed #fdba74;border-radius:10px;background:#fff7ed;">
          <tr>
            <td style="padding:18px;text-align:center;">
              <p style="margin:0 0 4px;font-size:12px;letter-spacing:0.08em;text-transform:uppercase;color:#c2410c;font-weight:700;">This week</p>
              <p style="margin:0;font-size:18px;font-weight:700;color:#9a3412;">Upgrade and ship more tests</p>
              <p style="margin:8px 0 0;font-size:13px;color:#9a3412;">Edit this box with your real promo details before sending.</p>
            </td>
          </tr>
        </table>
        <p style="margin:0;font-size:15px;line-height:1.6;color:#334155;">
          If you’ve been waiting to scale content production, this is a good moment to start.
        </p>`,
      ctaLabel: "See offer details →",
      ctaUrl: "{{login_url}}",
    }),
  },
  {
    id: "welcome-series",
    category: "Onboarding",
    badge: "Day 1",
    title: "Welcome series — get your first win",
    subject: "Your first Beyund AI win (takes ~10 minutes)",
    description: "Onboarding nurture for new signups — guide to first output.",
    audience: "free",
    html_body: shell({
      accent: "#1d4ed8",
      eyebrow: "Getting started",
      headline: "Ship your first asset today",
      bodyRows: `
        <p style="margin:0 0 14px;font-size:15px;line-height:1.6;color:#334155;">
          Welcome to Beyund AI. The fastest path to value is one finished piece of content — not a perfect campaign.
        </p>
        <p style="margin:0 0 8px;font-size:14px;font-weight:700;color:#0f172a;">Do this next (10 minutes):</p>
        <ol style="margin:0 0 16px;padding-left:20px;color:#334155;font-size:15px;line-height:1.65;">
          <li style="margin-bottom:8px;">Create or pick a character in Influencers / Image Studio</li>
          <li style="margin-bottom:8px;">Generate a short vertical clip in Video Studio</li>
          <li style="margin-bottom:0;">Download and post — then iterate the hook</li>
        </ol>
        <p style="margin:0;font-size:15px;line-height:1.6;color:#334155;">
          We’ll keep tips light and useful. You can change email preferences anytime.
        </p>`,
      ctaLabel: "Start your first creation →",
      ctaUrl: "{{login_url}}",
    }),
  },
];

export function fillCampaignPreview(html, overrides = {}) {
  const sample = {
    user_name: "Alex",
    user_email: "alex@example.com",
    login_url: "https://beyundai.app/user",
    site_url: "https://beyundai.app",
    unsubscribe_url: "https://beyundai.app/user?page=notification",
    campaign_title: "Campaign preview",
    date: new Date().toLocaleDateString(),
    ...overrides,
  };
  return String(html || "").replace(/\{\{\s*([^}]+)\s*\}\}/g, (_, key) => {
    const k = String(key).trim();
    return sample[k] != null ? String(sample[k]) : `{{${k}}}`;
  });
}
